import React, { useState, useCallback, memo } from 'react';
import TopBar from '../components/TopBar';
import { Link } from 'react-router-dom';

import VideoModal from './VideoModal';

interface FeatureCardProps {
    icon: string;
    title: string;
    description: string;
}

interface StepCardProps {
    number: string;
    title: string;
    description: string;
}

const HomePage: React.FC = () => {

    const [showVideo, setShowVideo] = useState(false);

    const handleVideoClick = useCallback(() => {
        setShowVideo(true);
    }, []);

    const handleCloseVideo = useCallback(() => {
        setShowVideo(false);
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
            <TopBar />

            {/* Hero Section */}
            <section className="relative py-20 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-900 tracking-tight">
                            Transform Your Content Into
                            <span className="block text-primary-600">Multiple Formats</span>
                        </h1>
                        <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
                            Automatically convert your videos into engaging social media posts, blog articles, and more. Save time and reach more audiences.
                        </p>
                        <div className="mt-10 flex justify-center space-x-4">
                            <Link
                                to="/signup"
                                className="px-8 py-3 text-base font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors duration-200 shadow-lg hover:shadow-xl"
                            >
                                Start Free Trial
                            </Link>
                            <button
                                onClick={handleVideoClick}
                                className="px-8 py-3 text-base font-medium text-primary-600 bg-white border-2 border-primary-600 rounded-md hover:bg-primary-50 transition-colors duration-200 shadow-lg hover:shadow-xl"
                            >
                                Watch Demo
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900">Powerful Features</h2>
                        <p className="mt-4 text-xl text-gray-600">Everything you need to maximize your content's reach</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <FeatureCard
                            icon="🎥"
                            title="Video Processing"
                            description="Upload your videos and let our AI handle the rest"
                        />
                        <FeatureCard
                            icon="📱"
                            title="Multi-Platform Output"
                            description="Generate content optimized for different social media platforms"
                        />
                        <FeatureCard
                            icon="⚡"
                            title="Fast & Efficient"
                            description="Get your content ready in minutes, not hours"
                        />
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900">How It Works</h2>
                        <p className="mt-4 text-xl text-gray-600">Simple steps to transform your content</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <StepCard
                            number="1"
                            title="Upload Your Video"
                            description="Simply drag and drop your video file or paste a YouTube URL"
                        />
                        <StepCard
                            number="2"
                            title="Choose Your Formats"
                            description="Select which platforms you want to target"
                        />
                        <StepCard
                            number="3"
                            title="Get Your Content"
                            description="Download your ready-to-use content in multiple formats"
                        />
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-primary-600">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl font-bold text-white">Ready to Transform Your Content?</h2>
                    <p className="mt-4 text-xl text-primary-100">Join thousands of content creators who are saving time and reaching more audiences</p>
                    <div className="mt-10">
                        <Link
                            to="/signup"
                            className="px-8 py-3 text-base font-medium text-primary-600 bg-white rounded-md hover:bg-primary-50 transition-colors duration-200 shadow-lg hover:shadow-xl"
                        >
                            Start Free Trial
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-gray-300 py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
                    <h3 className="text-lg font-semibold text-white mb-2 font-playfair">Content Repurposer</h3>
                    <p className="text-sm mb-2">Transform your content into multiple formats with AI-powered technology.</p>
                    <div className="border-t border-gray-800 w-full my-4"></div>
                    <p className="text-xs">&copy; {new Date().getFullYear()} Content Repurposer. All rights reserved.</p>
                </div>
            </footer>

            {showVideo && <VideoModal isOpen={showVideo} onClose={handleCloseVideo} />}
        </div>
    );
};

// Feature Card Component
const FeatureCard: React.FC<FeatureCardProps> = memo(({ icon, title, description }) => (
    <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
        <div className="text-4xl mb-4">{icon}</div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600">{description}</p>
    </div>
));

// Step Card Component
const StepCard: React.FC<StepCardProps> = memo(({ number, title, description }) => (
    <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
        <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xl font-bold mb-4">
            {number}
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600">{description}</p>
    </div>
));

export default HomePage; 